import { useState } from "react";
import { Radio, Send, Loader2, AlertTriangle, CheckCircle2, Users, Bell } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { toast } from "sonner";

export default function ServiceCallPanel() {
  const [message, setMessage] = useState("");
  const [includeAllievi, setIncludeAllievi] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [testingReminder, setTestingReminder] = useState(false);

  const send = async () => {
    if (!window.confirm("Confermi l'invio della chiamata in servizio a tutti gli operatori idonei?")) return;
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/discord/service-call", {
        custom_message: message.trim(),
        include_allievi: includeAllievi,
      });
      setResult(data);
      toast.success(`DM inviati: ${data.sent}/${data.total} (${data.failed} falliti)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'invio");
    } finally {
      setSending(false);
    }
  };

  const testReminder = async () => {
    if (!window.confirm("Vuoi inviare ORA il messaggio automatico delle 20:00 nel canale OCC (a solo scopo di verifica)?")) return;
    setTestingReminder(true);
    try {
      const { data } = await api.post("/admin/daily-reminder/test");
      toast.success(`Reminder di test inviato nel canale #${data.channel || "OCC"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio test fallito");
    } finally {
      setTestingReminder(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="service-call-panel">
      <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Radio className="h-4 w-4 text-sheriff-gold" />
          <h3 className="font-heading font-bold text-lg text-[#F5E6B8]">Chiamata in servizio (broadcast DM)</h3>
        </div>
        <p className="text-sm text-sheriff-gold/70 mb-4 leading-relaxed">
          Il bot <span className="text-sheriff-gold font-semibold">LSSD Administrator</span> manda un
          DM Discord a ogni operatore idoneo chiedendogli di entrare in servizio. Include un embed dorato
          con il codice radio <span className="font-mono">10-8</span> e la tua nota facoltativa.
        </p>

        <div className="space-y-2 mb-4">
          <label className="text-xs uppercase tracking-widest text-sheriff-gold/70 font-mono">
            Messaggio personale (facoltativo)
          </label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="es. Turnazione serale prevista. Presentarsi al più presto."
            data-testid="service-call-message"
            className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40"
          />
          <div className="text-[10px] text-sheriff-gold/40 text-right font-mono">{message.length}/500</div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-sheriff-gold/20 bg-[#0A1F18] px-4 py-3 mb-4">
          <div>
            <Label className="text-sm text-[#F5E6B8] font-semibold cursor-pointer">Includi Allievi</Label>
            <div className="text-[10px] text-sheriff-gold/50 font-mono mt-0.5">
              Se OFF, gli Allievi Vice Sceriffo vengono esclusi.
            </div>
          </div>
          <Switch checked={includeAllievi} onCheckedChange={setIncludeAllievi} data-testid="service-call-include-allievi" />
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-sheriff-gold/50 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Chi ha i DM chiusi non riceverà il messaggio
          </div>
          <Button
            onClick={send}
            disabled={sending}
            data-testid="service-call-send"
            className="bg-sheriff-gold text-[#0A1F18] hover:bg-sheriff-gold/90 font-bold"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Invia Chiamata a Tutti
          </Button>
        </div>
      </div>

      <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-5" data-testid="daily-reminder-test-panel">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-sheriff-gold/10 border border-sheriff-gold/30 p-2.5">
            <Bell className="h-4 w-4 text-sheriff-gold" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-[#F5E6B8] text-sm uppercase tracking-widest">
              Anteprima reminder 20:00
            </h3>
            <p className="text-xs text-sheriff-gold/60 mt-1 leading-relaxed">
              Invia subito nel canale OCC il messaggio automatico del Comando (bypassa il dedup giornaliero).
              Utile per verificare aspetto e contenuto prima delle 20:00.
            </p>
          </div>
          <Button
            onClick={testReminder}
            disabled={testingReminder}
            data-testid="daily-reminder-test-btn"
            variant="outline"
            className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 hover:text-sheriff-gold whitespace-nowrap"
          >
            {testingReminder ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Invia ora
          </Button>
        </div>
      </div>

      {result && (
        <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-6" data-testid="service-call-result">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-sheriff-gold" />
            <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-sheriff-gold">
              Esito invio
            </h3>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300/70 font-mono">Inviati</div>
              <div className="text-2xl font-heading font-black text-emerald-300 mt-1">{result.sent}</div>
            </div>
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3">
              <div className="text-[10px] uppercase tracking-widest text-rose-300/70 font-mono">Falliti</div>
              <div className="text-2xl font-heading font-black text-rose-300 mt-1">{result.failed}</div>
            </div>
            <div className="rounded-lg border border-sheriff-gold/40 bg-sheriff-gold/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-sheriff-gold/70 font-mono">Totale target</div>
              <div className="text-2xl font-heading font-black text-sheriff-gold mt-1">{result.total}</div>
            </div>
          </div>
          {result.details?.length > 0 && (
            <div className="max-h-64 overflow-y-auto space-y-1 text-xs font-mono">
              {result.details.map((d, i) => (
                <div key={i} className={`flex items-center gap-2 px-2 py-1 rounded ${d.ok ? "text-emerald-300" : "text-rose-300"}`}>
                  {d.ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                  <span className="flex-1 truncate">{d.user || "—"}</span>
                  <span className="text-sheriff-gold/40">{d.role || ""}</span>
                  {!d.ok && <span className="text-rose-400/60">· {d.reason}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
